import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Dialog, Transition } from "@headlessui/react";
import Button from "../TailwindComponents/Button";
import Badge from "../TailwindComponents/Badge";
import ProfileImage from "../../Header/ProfileImage";
import {
  PiXLight,
  PiUsersLight,
  PiCheckCircleFill,
  PiCircleLight,
  PiSparkleLight,
  PiMagnifyingGlassLight,
  PiWarningLight,
} from "react-icons/pi";
import {
  AddonAssignment,
  OrgMember,
} from "../../../store/actions/PlanActions";

/** Format snake_case role strings → Title Case (e.g. "full_access" → "Full Access") */
const formatRole = (role: string): string =>
  role
    .split("_")
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(" ");

interface AddonAssignmentModalProps {
  open: boolean;
  onClose: () => void;
  addon: AddonAssignment | null;
  members: OrgMember[];
  onAssign: (orgLicenseId: number, userId: number) => Promise<boolean>;
  onRevoke: (orgLicenseId: number, userId: number) => Promise<boolean>;
}

const AddonAssignmentModal: React.FC<AddonAssignmentModalProps> = ({
  open,
  onClose,
  addon,
  members,
  onAssign,
  onRevoke,
}) => {
  const { t } = useTranslation();
  const [loadingUserId, setLoadingUserId] = useState<number | null>(null);
  const [assignedIds, setAssignedIds] = useState<Set<number>>(new Set());
  const [searchQuery, setSearchQuery] = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState<number | null>(null);

  // Sync local assigned state from addon prop
  useEffect(() => {
    if (addon) {
      setAssignedIds(new Set(addon.assigned_users.map((u) => u.user_id)));
    }
  }, [addon]);

  // Reset search when modal opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setConfirmRevoke(null);
    }
  }, [open]);

  // Filter members by search
  const filteredMembers = useMemo(() => {
    if (!searchQuery.trim()) return members;
    const q = searchQuery.toLowerCase();
    return members.filter(
      (m) =>
        (m.name && m.name.toLowerCase().includes(q)) ||
        (m.email && m.email.toLowerCase().includes(q)) ||
        (m.role && formatRole(m.role).toLowerCase().includes(q))
    );
  }, [members, searchQuery]);

  // Sort: assigned users first
  const sortedMembers = useMemo(() => {
    return [...filteredMembers].sort((a, b) => {
      const aAssigned = assignedIds.has(a.id) ? 0 : 1;
      const bAssigned = assignedIds.has(b.id) ? 0 : 1;
      return aAssigned - bAssigned;
    });
  }, [filteredMembers, assignedIds]);

  if (!addon) return null;

  const usedSeats = assignedIds.size;
  const totalSeats = addon.quantity;
  const hasAvailable = usedSeats < totalSeats;

  const handleToggle = async (userId: number) => {
    // If removing, require confirmation first
    if (assignedIds.has(userId)) {
      if (confirmRevoke !== userId) {
        setConfirmRevoke(userId);
        return;
      }
      // Confirmed — proceed with revoke
      setConfirmRevoke(null);
    }

    setLoadingUserId(userId);
    try {
      if (assignedIds.has(userId)) {
        const ok = await onRevoke(addon.org_license_id, userId);
        if (ok) {
          setAssignedIds((prev) => {
            const next = new Set(prev);
            next.delete(userId);
            return next;
          });
        }
      } else {
        if (!hasAvailable) return;
        const ok = await onAssign(addon.org_license_id, userId);
        if (ok) {
          setAssignedIds((prev) => new Set(prev).add(userId));
        }
      }
    } finally {
      setLoadingUserId(null);
    }
  };

  return (
    <Transition show={open} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-3xl rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg flex flex-col" style={{ maxHeight: "85vh" }}>
              {/* Header */}
              <div className="flex items-center justify-between p-5 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-2 min-w-0">
                  <PiSparkleLight className="h-4 w-4 text-gray-500 dark:text-gray-400 flex-shrink-0" />
                  <Dialog.Title className="font-medium text-gray-900 dark:text-gray-100 text-sm truncate">
                    {addon.display_name || addon.name} — {t("Assign Users")}
                  </Dialog.Title>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800 flex-shrink-0"
                >
                  <PiXLight className="h-4 w-4 text-gray-400 dark:text-gray-500" />
                </button>
              </div>

              {/* Seat usage bar */}
              <div className="px-5 pt-4 pb-3">
                <div className="flex items-center justify-between text-xs mb-2">
                  <span className="text-gray-500 dark:text-gray-400 flex items-center gap-1">
                    <PiUsersLight className="h-3.5 w-3.5" />
                    {t("Seats")}
                  </span>
                  <span className="text-gray-900 dark:text-gray-100">
                    {usedSeats} / {totalSeats} {t("used")}
                  </span>
                </div>
                <div className="w-full bg-gray-100 dark:bg-gray-800 rounded-full h-1.5">
                  <div
                    className={`h-1.5 rounded-full transition-all ${
                      usedSeats >= totalSeats
                        ? "bg-red-500"
                        : usedSeats > totalSeats * 0.75
                        ? "bg-yellow-500"
                        : "bg-primary-500"
                    }`}
                    style={{ width: `${Math.min(100, (usedSeats / totalSeats) * 100)}%` }}
                  />
                </div>
                {!hasAvailable && (
                  <p className="text-xs text-red-500 dark:text-red-400 mt-1">
                    {t("All seats are assigned. Remove a user or add more seats to assign additional users.")}
                  </p>
                )}
              </div>

              {/* Search bar */}
              <div className="px-5 pb-3">
                <div className="relative">
                  <PiMagnifyingGlassLight className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400 dark:text-gray-500" />
                  <input
                    type="text"
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    placeholder={t("Search by name, email or role...")}
                    className="w-full pl-9 pr-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 placeholder-gray-400 dark:placeholder-gray-500 focus:ring-1 focus:ring-primary-500 focus:border-primary-500 outline-none"
                  />
                </div>
              </div>

              {/* Members list */}
              <div className="px-5 pb-3 flex-1 overflow-y-auto min-h-0">
                <div className="space-y-1">
                  {sortedMembers.map((member) => {
                    const isAssigned = assignedIds.has(member.id);
                    const isLoading = loadingUserId === member.id;
                    const canToggle = isAssigned || hasAvailable;
                    const showRevokeConfirm = confirmRevoke === member.id;

                    return (
                      <div key={member.id}>
                        <button
                          onClick={() => canToggle && handleToggle(member.id)}
                          disabled={isLoading || (!isAssigned && !hasAvailable)}
                          className={`w-full flex items-center gap-3 p-3 rounded-lg text-left transition-colors ${
                            isAssigned
                              ? "bg-primary-50 dark:bg-primary-900/10 border border-primary-200 dark:border-primary-800"
                              : canToggle
                              ? "hover:bg-gray-50 dark:hover:bg-gray-800 border border-transparent"
                              : "opacity-50 cursor-not-allowed border border-transparent"
                          }`}
                        >
                          {/* Toggle icon */}
                          <div className="flex-shrink-0">
                            {isLoading ? (
                              <div className="h-5 w-5 border-2 border-primary-500 border-t-transparent rounded-full animate-spin" />
                            ) : isAssigned ? (
                              <PiCheckCircleFill className="h-5 w-5 text-primary-500" />
                            ) : (
                              <PiCircleLight className="h-5 w-5 text-gray-300 dark:text-gray-600" />
                            )}
                          </div>

                          {/* Avatar */}
                          <ProfileImage
                            profilePicture={(member as any).avatarUrl || (member as any).profile_picture || null}
                            email={member.email}
                            initials={
                              member.name
                                ? member.name
                                    .split(" ")
                                    .map((n) => n[0])
                                    .join("")
                                    .toUpperCase()
                                    .slice(0, 2)
                                : (member.email?.[0] || "?").toUpperCase()
                            }
                            name={member.name}
                            size="h-8 w-8"
                          />

                          {/* User info */}
                          <div className="min-w-0 flex-1">
                            <p className="text-sm text-gray-900 dark:text-gray-100 truncate">
                              {member.name || member.email}
                            </p>
                            {member.name && (
                              <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                                {member.email}
                              </p>
                            )}
                          </div>

                          {/* Role badge */}
                          {member.role && (
                            <Badge color="gray" className="flex-shrink-0">
                              {formatRole(member.role)}
                            </Badge>
                          )}
                        </button>

                        {/* Revoke confirmation banner */}
                        {showRevokeConfirm && (
                          <div className="ml-11 mt-1 mb-1 flex items-center gap-2 rounded-lg border border-yellow-200 dark:border-yellow-800 bg-yellow-50 dark:bg-yellow-900/20 px-3 py-2">
                            <PiWarningLight className="h-4 w-4 text-yellow-600 dark:text-yellow-400 flex-shrink-0" />
                            <p className="text-xs text-yellow-700 dark:text-yellow-300 flex-1">
                              {t("Removing access may affect this user's workflow. Click again to confirm.")}
                            </p>
                            <button
                              onClick={(e) => { e.stopPropagation(); setConfirmRevoke(null); }}
                              className="text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200 font-medium"
                            >
                              {t("Cancel")}
                            </button>
                          </div>
                        )}
                      </div>
                    );
                  })}
                </div>

                {filteredMembers.length === 0 && members.length > 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                    {t("No members match your search")}
                  </p>
                )}

                {members.length === 0 && (
                  <p className="text-sm text-gray-500 dark:text-gray-400 text-center py-6">
                    {t("No members found in your organization")}
                  </p>
                )}
              </div>

              {/* Footer */}
              <div className="flex justify-end p-4 border-t border-gray-200 dark:border-gray-700">
                <Button variant="neutral" size="small" onClick={onClose}>
                  {t("Done")}
                </Button>
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default AddonAssignmentModal;
